// src/autoCardView.js — generic, form-AGNOSTIC COMPACT default view used when a card-variant boot has no
// registered view (e.g. a freshly-created card before generation, or a card whose design was deleted).
// Renders a small, glanceable summary that fits a ~320–350px sidebar slot — never the full-page autoView.

export function createAutoCardView(html, C) {
  const { Badge, KPI, StatGrid, FieldList } = C;

  return function autoCardView(d) {
    const meta = d.meta || {};
    const wo = d.workOrder;

    const numeric = d.fields().filter((f) => f && isScalar(f) && isNum(f.value));
    const hero = numeric[0]; // the single most prominent metric (deterministic: first numeric scalar)
    const supporting = numeric.slice(1, 3); // up to 2 secondary stats
    const scalars = d.fields().filter((f) => f && isScalar(f)).slice(0, 4);

    return html`<div class="aq-doc">
      <div class="aq-cardhead">
        <span class="aq-cardhead-t">${d.formName || 'Submission'}</span>
        ${meta.status ? html`<${Badge} text=${meta.status} tone=${meta.status} />` : null}
      </div>

      ${hero
        ? html`<div class="aq-row">
            <${KPI} label=${d.label(hero.fieldKey)} value=${d.num(hero.fieldKey)} />
          </div>`
        : null}

      ${hero && supporting.length
        ? html`<${StatGrid} items=${supporting.map((f) => ({ label: d.label(f.fieldKey), value: d.num(f.fieldKey) }))} />`
        : null}

      ${!hero && wo && wo.status
        ? html`<${StatGrid} items=${workOrderTiles(wo)} />`
        : null}

      ${!hero && !(wo && wo.status)
        ? html`<${FieldList} fields=${scalars} d=${d} />`
        : null}
    </div>`;
  };
}

// ---- helpers --------------------------------------------------------------
const SKIP = [
  'image', 'drawing', 'voice', 'video', 'document', 'file', 'signature',
  'repeatedform', 'repeated', 'attachment'
];

function isScalar(f) {
  const t = String(f.fieldType || '').toLowerCase();
  if (SKIP.includes(t)) return false;
  const v = f.value;
  if (Array.isArray(v) && v.length && typeof v[0] === 'object') return false;
  return true;
}

function isNum(v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return false;
  if (Array.isArray(v)) return false;
  return Number.isFinite(Number(v));
}

// Compact work-order tiles (status + priority) shown only when there is no numeric headline to lead with.
function workOrderTiles(wo) {
  const tiles = [{ label: 'Status', value: humanize(wo.status), tone: statusTone(wo.status) }];
  if (wo.priority) tiles.push({ label: 'Priority', value: humanize(wo.priority), tone: priorityTone(wo.priority) });
  return tiles;
}

function statusTone(s) {
  const t = String(s || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (['ok', 'success', 'approved', 'complete', 'completed', 'closed', 'done', 'passed', 'pass'].includes(t)) return 'ok';
  if (['inprogress', 'underreview', 'review', 'pending', 'draft', 'open', 'scheduled', 'warn', 'warning'].includes(t)) return 'warn';
  if (['rejected', 'failed', 'fail', 'cancelled', 'canceled', 'overdue', 'error', 'err'].includes(t)) return 'err';
  return '';
}

function priorityTone(p) {
  const t = String(p || '').toLowerCase();
  if (['high', 'urgent', 'critical'].includes(t)) return 'err';
  if (['medium', 'normal', 'moderate'].includes(t)) return 'warn';
  return '';
}

function humanize(s) {
  const str = String(s == null ? '' : s);
  if (!str) return str;
  return str.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

export default createAutoCardView;
